// Gets the tray's items ready while the person is still choosing, two at a time, so pressing Send
// rarely waits. Each result is kept per item and quality, so turning "Send original quality" on
// and off again does not redo the work.

import { preparePhoto } from "./image";
import type { Prepared, TrayItem } from "./tray";
import { describeVideo } from "./video";

export type PrepareFns = {
  photo: (file: Blob, original: boolean) => Promise<Prepared>;
  video: (file: Blob) => Promise<Prepared>;
};

export const defaultPrepareFns: PrepareFns = {
  photo: (file, original) => preparePhoto(file, { original }),
  video: async (file) => ({ file, kind: "video", meta: await describeVideo(file) }),
};

function fileMeta(file: File): Prepared {
  return {
    file,
    kind: "file",
    meta: { mime: file.type || "application/octet-stream", ...(file.name ? { name: file.name } : {}) },
  };
}

export class Preparer {
  private readonly results = new Map<string, Promise<Prepared>>();
  private readonly waiting: (() => void)[] = [];
  private running = 0;

  constructor(
    private readonly fns: PrepareFns = defaultPrepareFns,
    private readonly concurrency = 2,
  ) {}

  private key(item: TrayItem, original: boolean): string {
    // Only photos change with the quality switch.
    return item.kind === "photo" ? `${item.id}:${original ? "o" : "n"}` : item.id;
  }

  /** The item made ready. Started at most once per item and quality. */
  get(item: TrayItem, original: boolean): Promise<Prepared> {
    const key = this.key(item, original);
    const known = this.results.get(key);
    if (known) return known;
    const job = this.slot(() => {
      if (item.kind === "photo") return this.fns.photo(item.file, original);
      if (item.kind === "video") return this.fns.video(item.file);
      return Promise.resolve(fileMeta(item.file));
    });
    this.results.set(key, job);
    return job;
  }

  /** Whatever is already done for the item, without waiting. */
  peek(item: TrayItem, original: boolean): Promise<Prepared> | undefined {
    return this.results.get(this.key(item, original));
  }

  /** Forgets an item taken out of the tray. */
  forget(id: string): void {
    for (const key of [...this.results.keys()])
      if (key === id || key.startsWith(`${id}:`)) this.results.delete(key);
  }

  private async slot<T>(work: () => Promise<T>): Promise<T> {
    if (this.running >= this.concurrency) await new Promise<void>((resolve) => this.waiting.push(resolve));
    this.running++;
    try {
      return await work();
    } finally {
      this.running--;
      this.waiting.shift()?.();
    }
  }
}
